// Small, dismissable banner that appears at the bottom of the page when the
// browser fires `beforeinstallprompt`. Tapping "Install" triggers the native
// install flow. We hide ourselves after install or if the user dismisses.
//
// **Display rules** (iter 55):
//   - App shell + landing + free-plan owner's public site → banner CAN show
//   - Paid-plan owner's public site (`/site/<slug>` where owner is on
//     launch|growth|scale) → banner is HIDDEN, always. Paid customer sites
//     look clean and professional; no attribution.
//   - User dismissal is persistent for 14 days (unchanged).
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "iti.pwaDismissedAt";
// Re-show the banner after this many days (gives ample room for casual users)
const DISMISS_TTL_DAYS = 14;

function recentlyDismissed() {
  const raw = typeof window !== "undefined" ? localStorage.getItem(DISMISS_KEY) : null;
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (Number.isNaN(ts)) return false;
  const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_TTL_DAYS;
}

/**
 * True if the current path is a public customer site (`/site/<slug>*`).
 * Auth callback and system routes are NOT customer sites.
 */
function isPublicCustomerSitePath(pathname) {
  return /^\/site\/[^/]+/.test(pathname || "");
}

function extractSlug(pathname) {
  const m = /^\/site\/([^/?#]+)/.exec(pathname || "");
  return m ? m[1] : null;
}

export default function InstallPrompt() {
  const [available, setAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  // Tri-state: null = unknown (still fetching), true = allowed on this route,
  // false = suppress (paid customer site).
  const [allowedForRoute, setAllowedForRoute] = useState(true);
  const { pathname } = useLocation();

  // Route-aware suppression for paid customer sites. When the user visits
  // `/site/<slug>`, ping the public payload once and hide the banner if the
  // owner is on a paid plan. Landing / dashboard / template showroom /
  // free-owner sites all stay allowed (default `true`).
  useEffect(() => {
    let cancelled = false;
    if (!isPublicCustomerSitePath(pathname)) {
      setAllowedForRoute(true);
      return () => { cancelled = true; };
    }
    const slug = extractSlug(pathname);
    if (!slug) {
      setAllowedForRoute(true);
      return () => { cancelled = true; };
    }
    setAllowedForRoute(null); // "still checking" — suppress until we know
    (async () => {
      try {
        const { data } = await api.get(`/public/site/${slug}`);
        if (cancelled) return;
        // Explicit false → paid owner → hide. Anything else (true, missing,
        // or fetch error) → default to allowed so we never accidentally
        // hide the banner from a free-plan site.
        setAllowedForRoute(data?.show_install_banner !== false);
      } catch {
        if (!cancelled) setAllowedForRoute(true);
      }
    })();
    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onAvail = () => {
      if (window.__itiInstallPrompt && !recentlyDismissed()) setAvailable(true);
    };
    const onDone = () => setAvailable(false);

    // The prompt may already be available before this component mounts.
    onAvail();
    window.addEventListener("iti-install-available", onAvail);
    window.addEventListener("iti-install-completed", onDone);
    return () => {
      window.removeEventListener("iti-install-available", onAvail);
      window.removeEventListener("iti-install-completed", onDone);
    };
  }, []);

  const install = async () => {
    const evt = window.__itiInstallPrompt;
    if (!evt) return;
    setInstalling(true);
    try {
      evt.prompt();
      const choice = await evt.userChoice;
      if (choice?.outcome === "dismissed") {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } catch {
      /* user cancelled */
    }
    window.__itiInstallPrompt = null;
    setInstalling(false);
    setAvailable(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setAvailable(false);
  };

  if (!available) return null;
  // Route check: suppress on paid customer sites AND while we're still
  // determining ownership (prevents a flash of banner before hide).
  if (allowedForRoute !== true) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] iti-card flex items-center gap-3 px-4 py-3 max-w-md w-[92vw] shadow-xl"
      role="dialog"
      aria-label="Install Interest To Income"
    >
      <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700 shrink-0">
        <Download className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-900">Install Interest To Income</div>
        <div className="text-xs text-slate-600 truncate">One tap to open it like a real app — works offline too.</div>
      </div>
      <button
        onClick={install}
        disabled={installing}
        data-testid="pwa-install-btn"
        className="iti-btn-primary text-xs font-semibold px-3 py-2"
      >
        {installing ? "…" : "Install"}
      </button>
      <button
        onClick={dismiss}
        data-testid="pwa-dismiss-btn"
        aria-label="Dismiss install prompt"
        className="text-slate-400 hover:text-slate-700 p-1"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
